/**
 * Connected Services Page
 *
 * Allows users to connect/disconnect external services (Google Drive, Gmail, etc.)
 * Accessed via Settings > Connected Services tab or /settings/connected-services.
 *
 * Design ref: IMPL-drive-oauth-per-user.md, Phase 4, Section 4.2
 */
import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useSearchParams } from 'react-router-dom'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import {
  connectedServicesStore,
  type DriveConnectionStatus,
} from '@/stores/ConnectedServicesStore'
import {
  CloudArrowUpIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const ConnectedServicesPage = observer(() => {
  const [searchParams] = useSearchParams()
  const [connectMode, setConnectMode] = useState<'read' | 'readwrite'>('readwrite')

  useEffect(() => {
    connectedServicesStore.fetchServices()
    connectedServicesStore.fetchDriveStatus()

    // Handle callback query params (returned from Google OAuth redirect)
    const connected = searchParams.get('connected')
    const error = searchParams.get('error')
    const message = searchParams.get('message')
    const email = searchParams.get('email')

    if (connected === 'true') {
      toast.success(
        `Google Drive connected${email ? ` as ${email}` : ''}!`
      )
    } else if (error) {
      toast.error(message || 'Failed to connect Google Drive')
    }
  }, [searchParams])

  const driveStatus = connectedServicesStore.driveStatus

  const handleConnect = () => {
    connectedServicesStore.connectGoogleDrive(connectMode)
  }

  const handleDisconnect = () => {
    if (
      window.confirm(
        'Are you sure you want to disconnect Google Drive? ' +
          'Agents will no longer be able to access your Drive files.'
      )
    ) {
      connectedServicesStore.disconnectGoogleDrive(
        driveStatus?.mode === 'readwrite' ? 'readwrite' : 'read'
      )
    }
  }

  const handleReconnect = () => {
    connectedServicesStore.connectGoogleDrive(
      driveStatus?.mode === 'readwrite' ? 'readwrite' : 'read'
    )
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Connected Services
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect external services to enable agents to access your data.
          Tokens are encrypted at rest and never shared with other users.
        </p>
      </div>

      {/* Error banner */}
      {connectedServicesStore.error && (
        <div className="rounded-lg bg-error-100 p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-error-800 mt-0.5" />
          <div>
            <p className="text-sm text-error-800">
              {connectedServicesStore.error}
            </p>
            <button
              onClick={() => connectedServicesStore.clearError()}
              className="mt-1 text-xs text-error-800 underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Google Drive Card */}
      <Card padding="none" className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary-500/15 p-3">
              <CloudArrowUpIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                Google Drive
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Allow Drive Reader and Drive Writer agents to access your Google Drive files.
              </p>
            </div>
          </div>

          {/* Status badge */}
          <div>
            {driveStatus?.connected && driveStatus?.status === 'active' ? (
              <Badge variant="success">Connected</Badge>
            ) : driveStatus?.status === 'expired' ? (
              <Badge variant="warning">Expired</Badge>
            ) : (
              <Badge variant="gray">Not Connected</Badge>
            )}
          </div>
        </div>

        {/* Connection details (when connected) */}
        {driveStatus?.connected && (
          <div className="mt-4 ml-16 space-y-2">
            <div className="flex items-center gap-2 text-sm text-foreground">
              <CheckCircleIcon className="h-4 w-4 text-success-800" />
              <span>
                Mode: {driveStatus.mode === 'readwrite' ? 'Read + Write' : 'Read Only'}
              </span>
            </div>
            {driveStatus.provider_email && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <LinkIcon className="h-4 w-4 text-muted-foreground" />
                <span>Account: {driveStatus.provider_email}</span>
              </div>
            )}
            {driveStatus.last_used && (
              <div className="text-sm text-muted-foreground ml-6">
                Last used: {formatDate(driveStatus.last_used)}
              </div>
            )}
            {!driveStatus.token_valid && (
              <div className="flex items-center gap-2 text-sm text-warning-800">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>Token may need refresh. It will auto-refresh on next use.</span>
              </div>
            )}
          </div>
        )}

        {/* Expired warning */}
        {driveStatus?.status === 'expired' && (
          <div className="mt-4 ml-16 rounded-lg bg-warning-100 p-3">
            <div className="flex items-center gap-2 text-sm text-warning-800">
              <ExclamationTriangleIcon className="h-4 w-4" />
              <span>
                Your Google Drive connection has expired
                {driveStatus.provider_email
                  ? ` (${driveStatus.provider_email})`
                  : ''}
                . Please reconnect to restore agent access.
              </span>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-6 ml-16 flex items-center gap-3">
          {!driveStatus?.connected ? (
            <>
              <div className="flex items-center gap-2 mr-4">
                <label className="text-sm text-muted-foreground">
                  Access level:
                </label>
                <select
                  value={connectMode}
                  onChange={(e) =>
                    setConnectMode(e.target.value as 'read' | 'readwrite')
                  }
                  className="text-sm rounded-md border border-border bg-card px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="readwrite">Read + Write</option>
                  <option value="read">Read Only</option>
                </select>
              </div>
              <Button
                onClick={handleConnect}
                disabled={connectedServicesStore.isLoading}
                isLoading={connectedServicesStore.isLoading}
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Connect Google Drive
              </Button>
            </>
          ) : driveStatus?.status === 'expired' ? (
            <Button onClick={handleReconnect}>
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Reconnect
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleReconnect}
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reconnect
              </Button>
              <Button
                variant="danger"
                onClick={handleDisconnect}
              >
                <XCircleIcon className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Future services placeholder: Gmail */}
      <Card padding="none" className="p-6 opacity-60">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-muted p-3">
            <CloudArrowUpIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Gmail
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Allow agents to read and send emails on your behalf.
            </p>
            <Badge variant="gray" className="mt-2">
              Coming Soon
            </Badge>
          </div>
        </div>
      </Card>

      {/* Future services placeholder: OneDrive */}
      <Card padding="none" className="p-6 opacity-60">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-muted p-3">
            <CloudArrowUpIcon className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              OneDrive
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Allow agents to access your Microsoft OneDrive files.
            </p>
            <Badge variant="gray" className="mt-2">
              Coming Soon
            </Badge>
          </div>
        </div>
      </Card>
    </div>
  )
})

export default ConnectedServicesPage
